'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const HeroSection: React.FC = () => {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [headlineRef.current, subRef.current, statsRef.current, ctaRef.current];
    els.forEach((el, i) => {
      if (!el) return;
      setTimeout(() => {
        el.classList.add('visible');
      }, 150 + i * 180);
    });
  }, []);

  return (
    <section className="relative min-h-screen flex items-end pb-24 overflow-hidden">
      {/* Cinematic background image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1606467493403-bd3bfc99622a"
          alt="Semi-truck on open highway at night with dashboard glow and road stretching into darkness"
          fill
          className="object-cover brightness-[0.45]"
          priority />
        
        {/* Radial violet glow from center-bottom */}
        <div className="absolute inset-0 hero-glow" />
        {/* Gradient fade to plum at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-plum-black via-plum-black/30 to-transparent" />
        {/* Noise texture */}
        <div className="absolute inset-0 noise-overlay opacity-30" />
      </div>

      {/* Dot pattern overlay */}
      <div className="absolute inset-0 dot-pattern z-0 opacity-40" />

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="max-w-4xl space-y-8">
          {/* Section tag */}
          <div className="flex items-center gap-3 reveal">
            <div className="w-2 h-2 rounded-full bg-violet animate-pulse" />
            <span className="text-[11px] font-bold uppercase tracking-[0.4em] text-silver-lavender/70">
              Owner-Operator Freight Program · Est. 2022
            </span>
          </div>

          {/* Main headline */}
          <h1
            ref={headlineRef}
            className="reveal text-hero font-bold leading-[0.88] tracking-tight text-cream"
            style={{ textShadow: '0 2px 40px rgba(123,47,190,0.3)' }}>
            Reliable Freight.
            <br />
            <span className="text-silver-lavender/80">Flexible Options.</span>
            <br />
            Built Around Your Business.
          </h1>

          {/* Sub-headline */}
          <p
            ref={subRef}
            className="reveal reveal-delay-2 text-xl text-silver-lavender/70 max-w-2xl leading-relaxed font-light">
            Since 2022, we have been helping Owner-Operators keep their trucks moving
            with reliable freight opportunities across OTR and regional markets
            Amazon Relay Contract Lanes, Street Loads, and Spot Loads.
            Your truck. Your goals. Your operating plan.
          </p>

          {/* Stats row */}
          <div
            ref={statsRef}
            className="reveal reveal-delay-3 flex flex-wrap gap-8 pt-4">
            {[
              { value: '90%', label: 'Gross Revenue (Amazon)' },
              { value: '$8K–$11K', label: 'Avg Weekly Gross' },
              { value: '$380/wk', label: 'Fixed Deductions' },
              { value: 'Est. 2022', label: 'In Business' },
            ].map((stat) =>
              <div key={stat.label} className="space-y-1">
                <p className="text-3xl font-bold text-cream tracking-tight">{stat.value}</p>
                <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/50">{stat.label}</p>
              </div>
            )}
          </div>

          {/* CTA buttons */}
          <div ref={ctaRef} className="reveal reveal-delay-4 pt-2 flex flex-col sm:flex-row gap-4">
            <a
              href="#cta-section"
              className="bg-violet text-cream px-8 py-4 text-[11px] font-bold uppercase tracking-widest rounded-full hover:bg-violet-bright transition-all duration-300 whitespace-nowrap cta-glow text-center">
              Apply Now
            </a>
            <a
              href="#contact"
              className="bg-transparent text-cream border border-silver-lavender/30 px-8 py-4 text-[11px] font-bold uppercase tracking-widest rounded-full hover:border-violet/60 hover:bg-violet/10 transition-all duration-300 whitespace-nowrap text-center">
              Contact Recruiting
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;